import axios from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import {
	getCurrencyListRequest,
	getCurrencyListSuccess,
	getCurrencyListFailure,
	getCurrencyDetailsRequest,
	getCurrencyDetailsSuccess,
	getCurrencyDetailsFailure,
	updateCurrencyRequest,
	saveCurrencySuccess,
	saveCurrencyFailure,
} from './reducer';
import { getCurrencyLastListParams } from './selectors';
import {
	TCurrencyDetails,
	TCurrencyIdRequest,
	TCurrencyListResponse,
	TCurrencyManagementRequest,
	TUpdateCurrencyRequest,
} from './types';

const DEFAULT_LIST_PARAMS: TCurrencyManagementRequest = {
	pageSize: PAGE_SIZE,
	pageNumber: 1,
};

/** Shows the message the API returned, falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	if (axios.isAxiosError(error)) {
		notificationContainer(
			String(error?.response?.data?.message ?? error.message ?? fallback),
			'error',
		);
		return;
	}
	notificationContainer(fallback, 'error');
}

function* getCurrencyListWorker({ payload }: PayloadAction<TCurrencyManagementRequest>) {
	try {
		const response: TCurrencyListResponse = yield call(api.currency.getCurrencyList, payload);
		yield put(getCurrencyListSuccess(response));
	} catch (error) {
		yield put(getCurrencyListFailure());
		notifyError(error, 'Failed to load currencies');
	}
}

function* refetchCurrencyList() {
	const lastParams: TCurrencyManagementRequest | null = yield select(getCurrencyLastListParams);
	const response: TCurrencyListResponse = yield call(
		api.currency.getCurrencyList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getCurrencyListSuccess(response));
}

function* getCurrencyDetailsWorker({ payload }: PayloadAction<TCurrencyIdRequest>) {
	try {
		const response: TCurrencyDetails = yield call(api.currency.getCurrencyDetails, payload);
		yield put(getCurrencyDetailsSuccess(response));
	} catch (error) {
		yield put(getCurrencyDetailsFailure());
		notifyError(error, 'Failed to load currency');
	}
}

function* updateCurrencyWorker({ payload }: PayloadAction<TUpdateCurrencyRequest>) {
	try {
		const response: TCurrencyDetails = yield call(api.currency.updateCurrency, payload);
		yield put(getCurrencyDetailsSuccess(response));
		yield call(refetchCurrencyList);
		yield put(saveCurrencySuccess());
		notificationContainer('Currency was successfully updated', 'success');
	} catch (error) {
		yield put(saveCurrencyFailure());
		notifyError(error, 'Failed to update currency');
	}
}

export function* currencySaga() {
	yield takeLatest(getCurrencyListRequest.type, getCurrencyListWorker);
	yield takeLatest(getCurrencyDetailsRequest.type, getCurrencyDetailsWorker);
	yield takeLatest(updateCurrencyRequest.type, updateCurrencyWorker);
}
